/**
 * Криптография обмена ключами и личности пользователя (libsodium).
 *
 * Модель простая и осознанно не полная:
 *
 *   личность    — пара X25519 на пользователя; публичный ключ лежит на сервере,
 *                 приватный зашифрован ключом из пароля;
 *   ключ чата   — 32 случайных байта, общие для участников, запечатанные
 *                 crypto_box_seal под публичный ключ каждого;
 *   сообщения   — AES-256-GCM ключом чата (см. message-crypto.ts).
 *
 * Чего здесь НЕТ: прямой секретности (forward secrecy). Ключ чата живёт до
 * следующей смены эпохи, и тот, кто добудет его вместе с архивом переписки,
 * прочитает всю эпоху целиком. Смена ключа при изменении состава ограничивает
 * ущерб, но не заменяет двойной храповик. Это сознательный размен на первую
 * версию, а не недосмотр.
 */
import { fromBase64, toBase64 } from './bytes.js';

/**
 * Параметры Argon2id по умолчанию.
 *
 * 64 МБ, а не «моderate» 256 МБ, ровно по одной причине: 256 МБ выделения в
 * WASM роняют вкладку на телефонах, а мессенджер должен открываться в первую
 * очередь на телефоне. Недобор памяти компенсируем числом проходов.
 */
export const DEFAULT_PW_OPS = 4;
export const DEFAULT_PW_MEM = 64 * 1024 * 1024;

async function loadSodium() {
  const module = await import('libsodium-wrappers-sumo');
  await module.default.ready;
  return module.default;
}

let readyPromise: ReturnType<typeof loadSodium> | null = null;

/**
 * libsodium грузится отдельным чанком и только когда действительно нужен.
 *
 * Он весит около 700 КБ и требуется ровно в двух местах: при входе
 * (Argon2id над паролем) и при обмене ключами чатов. Чтение и получение
 * сообщений идут на WebCrypto и без него — поэтому держать его в основном
 * бандле значило бы замедлять каждый запуск ради редких операций.
 */
export function sodium(): ReturnType<typeof loadSodium> {
  readyPromise ??= loadSodium();
  return readyPromise;
}

export interface DerivedKeys {
  /** Уезжает на сервер вместо пароля. */
  authKey: Uint8Array;
  /** Не покидает устройство. Им зашифрован приватный ключ личности. */
  wrapKey: Uint8Array;
}

/**
 * Пароль → два независимых ключа.
 *
 * Argon2id даёт мастер-ключ, из него keyed-BLAKE2b с разными метками разводит
 * два подключа. Знание authKey (то есть содержимого серверной базы) ничего не
 * говорит о wrapKey, поэтому утечка базы не раскрывает приватные ключи.
 */
export async function deriveKeys(
  password: string,
  saltB64: string,
  opsLimit: number,
  memLimit: number,
): Promise<DerivedKeys> {
  const s = await sodium();
  const salt = fromBase64(saltB64);

  const masterKey = s.crypto_pwhash(
    32,
    password,
    salt,
    opsLimit,
    memLimit,
    s.crypto_pwhash_ALG_ARGON2ID13,
  );

  return {
    authKey: s.crypto_generichash(32, 'mess/auth/v1', masterKey),
    wrapKey: s.crypto_generichash(32, 'mess/wrap/v1', masterKey),
  };
}

export interface Identity {
  publicKey: Uint8Array;
  secretKey: Uint8Array;
}

export async function createIdentity(): Promise<Identity> {
  const s = await sodium();
  const pair = s.crypto_box_keypair();
  return { publicKey: pair.publicKey, secretKey: pair.privateKey };
}

export async function newSalt(): Promise<string> {
  const s = await sodium();
  return toBase64(s.randombytes_buf(16));
}

/** Приватный ключ под ключом из пароля: nonce ‖ шифротекст, 72 байта. */
export async function wrapIdentitySecret(
  secretKey: Uint8Array,
  wrapKey: Uint8Array,
): Promise<string> {
  const s = await sodium();
  const nonce = s.randombytes_buf(s.crypto_secretbox_NONCEBYTES);
  const box = s.crypto_secretbox_easy(secretKey, nonce, wrapKey);

  const blob = new Uint8Array(nonce.length + box.length);
  blob.set(nonce);
  blob.set(box, nonce.length);
  return toBase64(blob);
}

/** Возвращает null при неверном пароле — именно так и проверяется вход. */
export async function unwrapIdentitySecret(
  blobB64: string,
  wrapKey: Uint8Array,
): Promise<Uint8Array | null> {
  const s = await sodium();
  const blob = fromBase64(blobB64);
  const nonceLength = s.crypto_secretbox_NONCEBYTES;
  if (blob.length <= nonceLength) return null;

  try {
    return s.crypto_secretbox_open_easy(blob.subarray(nonceLength), blob.subarray(0, nonceLength), wrapKey);
  } catch {
    return null;
  }
}

export async function randomChatKey(): Promise<Uint8Array> {
  const s = await sodium();
  return s.randombytes_buf(32);
}

/**
 * Запечатывает ключ чата под публичный ключ получателя.
 *
 * crypto_box_seal — анонимный ящик: отправитель использует эфемерную пару,
 * которую тут же выбрасывает. Сервер видит 80 непрозрачных байт и не может
 * ни открыть их, ни установить, кто запечатывал.
 */
export async function sealChatKey(
  chatKey: Uint8Array,
  recipientPublicKeyB64: string,
): Promise<string> {
  const s = await sodium();
  return toBase64(s.crypto_box_seal(chatKey, fromBase64(recipientPublicKeyB64)));
}

export async function openChatKey(
  sealedB64: string,
  identity: Identity,
): Promise<Uint8Array | null> {
  const s = await sodium();
  try {
    return s.crypto_box_seal_open(fromBase64(sealedB64), identity.publicKey, identity.secretKey);
  } catch {
    return null;
  }
}
